import React from 'react';
import { useStaticQuery, graphql, Link, navigate } from 'gatsby';
import { HidingHeader } from 'hiding-header-react';
import { keyframes } from '@emotion/react';
import Container from '../primitives/grid/container';
import {
    Drawer,
    DrawerContent,
} from '../primitives/drawer';
import FadeTransition from '../animations/transitions/fade-transition';
import wordpressFlatMenuToHierarchical, {
    ParsedMenuItem,
} from '../../utils/wordpress-flat-menu-to-hierarchical';
import BookIcon from '../../images/book-icon.svg';
import ContrastIcon from '../../images/contrast-icon.svg';
import EnglishIcon from '../../images/english-icon.svg';
import GermanIcon from '../../images/english-icon.svg';
import getLangAndTranslation from '../../utils/get-lang-and-translation';
import VisuallyHidden from '../primitives/visually-hidden';
import SearchIcon from '../../images/search.svg';
import Logo from '../../images/logo.svg';
import ScrollEffect from '../animations/components/scroll-effect';
import ArrowUPRightIcon from '../../images/arrow-up-right-regular.svg';
import ArrowRightIcon from '../../images/arrow-right-light.svg';
import NotfallIcon from '../../images/notfall-icon.svg';
import StyledSearchInput from '../primitives/styled-search-input';
import { StyledIconButtonColored } from '../primitives/styled-icon-button';
import FormProvider from '../primitives/form-provider';
import { useSearchTermState } from '../../utils/use-search-builder';
import isMainSite from '../../utils/is-main-site';
import GermanSiteLogo from '../../images/german-site-logo.svg';
import LichtensteinSiteLogo from '../../images/lichtenstein-site-logo.svg';
import GesellschaftSiteLogo from '../../images/gesellschaft-site-logo.svg';
import { GatsbyImageSVGFallback } from '../primitives/image/gatsby-image-svg-fallback';
import { useContrast } from '../theme/contrast-context';
import MobileLinkIcon from '../../images/mobile-link-icon.svg';

interface MobileHeaderProps {
    language?: {
        slug: 'en' | 'de'
    }
}

const ToggleButton = ({ setOpen, setSearch, open }) => (
    <button
        type="button"
        onClick={() => {
            setOpen(!open);
            setSearch(false);
        }}
        css={{
            marginLeft: '18px',
            height: '16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        }}
    >
        <VisuallyHidden>
            Toggle Menu
        </VisuallyHidden>
        <div css={{
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-schwarz)',
            transition: '0.3s',
            transform: open ? 'rotate(45deg) translateX(4px) translateY(-6px)' : 'rotate(0deg)',
            transformOrigin: 'center left',
        }}
        />
        <div css={{
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-schwarz)',
            transition: '0.3s',
            opacity: open ? '0' : '1',
        }}
        />
        <div css={{
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-schwarz)',
            transition: '0.3s',
            transform: open ? 'rotate(-45deg) translateX(5px) translateY(5px)' : 'rotate(0deg)',
            transformOrigin: 'center left',
        }}
        />
    </button>
);

const MobileHeader = ({ language }: MobileHeaderProps) => {
    const [menuLevel, setMenuLevel] = React.useState(0);
    const [selectedMenuList, setSelectedMenuList] = React.useState([]);
    const [menuChildren, setMenuChildren] = React.useState([]);
    const [selectedIndicesList, setSelectedIndicesList] = React.useState([]);
    const [subMenuHeading, setSubMenuHeading] = React.useState(0);
    const [search, setSearch] = React.useState(false);
    const [headerOpen, setHeaderOpen] = React.useState(true);

    const { lang } = getLangAndTranslation({ language });

    const menuData = useStaticQuery(graphql`
        {
            englishMenu: wpMenu(locations: {eq: PRIMARY_MENU___EN}) {
                menuItems {
                    nodes {
                        label
                        url
                        id
                        parentId
                    }
                }
            }
            deutschMenu: wpMenu(locations: {eq: PRIMARY_MENU}) {
                menuItems {
                    nodes {
                        label
                        url
                        id
                        parentId
                    }
                }
            }
            wp {
                siteOptions {
                    websiteFunktionen {
                        enableEmergency
                        enableSearch
                    }
                    seiteLogos {
                        logos {
                            image {
                                localFile {
                                    publicURL
                                }
                            }
                            link
                        }
                    }
                }
            }
        }
  `);

    const enableEmergency = menuData?.wp?.siteOptions?.websiteFunktionen?.enableEmergency;
    const enableSearch = menuData?.wp?.siteOptions?.websiteFunktionen?.enableSearch;

    const rawMenusEn = menuData?.englishMenu?.menuItems?.nodes;
    const rawMenusDe = menuData?.deutschMenu?.menuItems?.nodes;
    const siteLogos = menuData?.wp?.siteOptions?.seiteLogos?.logos;

    const menuItems = wordpressFlatMenuToHierarchical(lang === 'en' && rawMenusEn?.length > 0 ? rawMenusEn : rawMenusDe);

    const drawerRef = React.useRef<HTMLDivElement>(null);

    const [open, setOpen] = React.useState(false);

    const [searchQuery, setSearchQuery] = useSearchTermState();

    const { switchTheme } = useContrast();

    React.useEffect(() => {
        if (open) {
            setMenuLevel(0);
        }
    }, [open]);

    const fadein = keyframes`
        from, 80% {
            opacity: 0
        }
        to {
            opacity: 1
        }
        `;

    return (
        <>
            <div ref={drawerRef} />
            <HidingHeader className={headerOpen ? 'hidingHeader open' : 'hidingHeader closed'} onVisibleHeightChange={val => setHeaderOpen(val > 50)} css={{ zIndex: 2500 }}>
                <div
                    css={{
                        width: '100%',
                        height: 'auto',
                        display: 'none',
                        position: 'relative',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor: 'var(--color-gray-white)',
                        ...!isMainSite() ? {
                            '@media (max-width: 992px)': {
                                display: 'flex',
                            },
                        } : {
                            '@media (max-width: 1024px)': {
                                display: 'flex',
                            },
                        },
                        pointerEvents: 'auto',
                    }}
                >
                    <div
                        css={{
                            width: '100%',
                            height: 'auto',
                            display: 'flex',
                            boxShadow: '0px 3px 20px rgba(0, 0, 0, 0.0784314)',
                            alignItems: 'flex-start',
                            flexDirection: 'column',
                            zIndex: '2001',
                            position: 'relative',
                        }}
                    >
                        <Container>
                            <div
                                css={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '18px 0',
                                }}
                            >
                                <ScrollEffect
                                    runOnce
                                    inViewProperties={{ x: [-10, 0], opacity: [0, 1] }}
                                    css={{
                                        '& > div': {
                                            position: 'relative',
                                        },
                                    }}
                                >
                                    {siteLogos.length > 0 && (
                                        <Link
                                            key={siteLogos[0]?.image?.localFile.publicURL}
                                            to={lang === 'en' ? '/en' : siteLogos[0]?.link}
                                            css={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '0',
                                                transform: 'translateY(-50%)',
                                            }}
                                        >
                                            <GatsbyImageSVGFallback
                                                src={siteLogos[0]?.image?.localFile.publicURL}
                                                css={{ width: 'fit-content', maxWidth: '175px', height: '35px' }}
                                            />
                                        </Link>
                                    )}
                                </ScrollEffect>
                                <div
                                    css={{
                                        width: 'fit-content',
                                        height: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {enableSearch && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearch(!search);
                                                setOpen(false);
                                            }}
                                        >
                                            <SearchIcon css={{ width: '20px' }} />
                                            <VisuallyHidden>
                                                Suche
                                            </VisuallyHidden>
                                        </button>
                                    )}
                                    {enableEmergency && (
                                        <Link
                                            to="/notfall"
                                            css={{
                                                width: '22px',
                                                height: '22px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginLeft: '16px',
                                                borderRadius: '50%',
                                                justifyContent: 'center',
                                                backgroundColor: 'var(--color-red-drkrot)',
                                                padding: '5px',
                                            }}
                                        >
                                            <NotfallIcon />
                                        </Link>
                                    )}
                                    <ToggleButton setOpen={setOpen} setSearch={setSearch} open={open} />
                                </div>
                            </div>
                            {enableSearch && (
                                <div css={{
                                    width: '100%', overflow: 'hidden', height: search ? '60px' : '0px', transition: '0.3s', opacity: search ? 1 : 0,
                                }}
                                >
                                    <form onSubmit={(e) => {
                                        e.preventDefault();
                                        navigate(`/suche/?query=${searchQuery}`);
                                    }}
                                    >
                                        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: '12px', paddingTop: '10px' }}>
                                            <StyledSearchInput
                                                value={searchQuery}
                                                onChange={(e) => {
                                                    setSearchQuery(e?.target?.value);
                                                }}
                                                name="search"
                                                placeholder="Suche"
                                            />
                                            <StyledIconButtonColored
                                                type="submit"
                                                onClick={() => {
                                                    setOpen(false);
                                                }}
                                                css={{
                                                    marginLeft: '9px',
                                                    color: '#47474A',
                                                }}
                                            >
                                                <SearchIcon css={{ width: '20px' }} />
                                                <VisuallyHidden>
                                                    Suche
                                                </VisuallyHidden>
                                            </StyledIconButtonColored>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </Container>
                    </div>
                </div>
            </HidingHeader>
            {drawerRef?.current && (
                <Drawer open={open}>
                    <DrawerContent
                        placement="right"
                        size="full"
                        animation="slide"
                        container={drawerRef?.current || undefined}
                        css={{
                            boxShadow: '0 3px 20px rgb(0, 0, 0, 0.08)',
                            maxWidth: '450px',
                        }}
                    >
                        <Container
                            className="p1"
                            css={{
                                height: '100%', display: 'flex', flexDirection: 'column', paddingTop: '98px', overflowY: 'auto',
                            }}
                        >
                            <div css={{ position: 'absolute', top: '22px', right: '35px', animation: `${fadein} 1s `, opacity: open ? undefined : 0 }}>
                                <ToggleButton setOpen={setOpen} setSearch={setSearch} open={open} />
                            </div>
                            {menuLevel > 0 && (
                                <div
                                    css={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        position: 'relative',
                                    }}
                                >
                                    <FadeTransition shouldChange={String(menuLevel)}>
                                        <ScrollEffect
                                            inViewProperties={{
                                                x: [10, 0],
                                                opacity: [0, 1],
                                            }}
                                            css={{ width: '100%' }}
                                        >
                                            <button
                                                onClick={() => {
                                                    setMenuLevel(prev => prev - 1);
                                                    if (subMenuHeading && menuLevel === 1) {
                                                        setSubMenuHeading(null);
                                                    }
                                                    if (menuLevel === 1) {
                                                        setSelectedMenuList(prev => selectedMenuList.filter(
                                                            (_, index) => index !== selectedMenuList.length - 1,
                                                        ));
                                                    }
                                                    const filteredIndices = selectedIndicesList.filter(
                                                        (_, index) => index !== selectedIndicesList.length - 1,
                                                    );
                                                    setSelectedIndicesList(prev => filteredIndices);
                                                }}
                                                css={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    borderBottom: '1px solid #e2e2e2',
                                                    marginBottom: '17px',
                                                    paddingBottom: '17px',
                                                    justifyContent: 'flex-start',
                                                    flexDirection: 'row',
                                                    color: 'var(--color-blue-mittelblau)',
                                                }}
                                            >
                                                <ArrowRightIcon css={{ transform: 'rotate(180deg)', width: '21px' }} />
                                                <span
                                                    css={{
                                                        fontWeight: 600,
                                                        paddingLeft: '10px',
                                                    }}
                                                >
                                                    {selectedMenuList
                                                    && selectedMenuList?.length > 0
                                                    && selectedMenuList[selectedMenuList?.length - 1]}
                                                </span>
                                            </button>
                                        </ScrollEffect>
                                    </FadeTransition>
                                </div>
                            )}
                            <div
                                css={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    width: '100%',
                                }}
                            >
                                <div css={{ width: '100%' }}>
                                    <div
                                        css={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            marginBottom: '18px',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {menuLevel === 0
                                        && menuItems?.map((firstMenuLevel, index) => (
                                            <>
                                                {firstMenuLevel?.childMenu?.length >= 1 ? (
                                                    <ScrollEffect
                                                        key={index}
                                                        inViewProperties={{
                                                            y: [10, 0],
                                                            opacity: [0, 1],
                                                        }}
                                                        inViewDelay={index * 0.05}
                                                        css={{ width: '100%' }}
                                                    >
                                                        <button
                                                            key={index}
                                                            onClick={() => {
                                                                setMenuLevel(1);
                                                                setSelectedMenuList(prev => [
                                                                    ...prev,
                                                                    firstMenuLevel?.label,
                                                                ]);
                                                                setMenuChildren(firstMenuLevel?.childMenu);
                                                                setSelectedIndicesList(prev => [...prev, index]);
                                                            }}
                                                            css={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderBottom: '1px solid #e2e2e2',
                                                                marginBottom: '15px',
                                                                paddingBottom: '15px',
                                                                justifyContent: 'space-between',
                                                                flexDirection: 'row',
                                                                color: 'var(--color-gray-schwarz)',
                                                            }}
                                                        >
                                                            <span
                                                                css={{
                                                                    fontWeight: 600,
                                                                    paddingRight: '5px',
                                                                }}
                                                            >
                                                                {firstMenuLevel.label}
                                                            </span>
                                                            <ArrowRightIcon css={{ width: '21px', color: 'var(--color-gray-grau)' }} />
                                                        </button>
                                                    </ScrollEffect>
                                                ) : (
                                                    <ScrollEffect
                                                        inViewProperties={{
                                                            y: [10, 0],
                                                            opacity: [0, 1],
                                                        }}
                                                        inViewDelay={index * 0.05}
                                                        css={{ width: '100%' }}
                                                        key={index}
                                                    >
                                                        <Link
                                                            to={firstMenuLevel.url}
                                                            onClick={() => {
                                                                setOpen(false);
                                                            }}
                                                            css={{ width: '100%' }}
                                                        >
                                                            <div
                                                                key={index}
                                                                css={{
                                                                    width: '100%',
                                                                    height: 'auto',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid #e2e2e2',
                                                                    marginBottom: '15px',
                                                                    paddingBottom: '15px',
                                                                    justifyContent: 'space-between',
                                                                    flexDirection: 'row',
                                                                }}
                                                            >
                                                                <span
                                                                    css={{
                                                                        fontWeight: 600,
                                                                        paddingRight: '5px',
                                                                        '@media (max-width: 767px)': {
                                                                            color: 'var(--color-gray-schwarz)',
                                                                        },
                                                                    }}
                                                                >
                                                                    {firstMenuLevel.label}
                                                                </span>
                                                                {Array.from(firstMenuLevel.url)[0] !== '/' && Array.from(firstMenuLevel.url)[0] !== '#' && <MobileLinkIcon css={{ color: 'var(--color-gray-grau)' }} />}
                                                            </div>
                                                        </Link>
                                                    </ScrollEffect>
                                                )}
                                            </>
                                        ))}
                                        {menuLevel > 0
                                        && menuChildren?.map((subMenuItem, index) => (
                                            <>
                                                {subMenuItem?.childMenu?.length >= 1 ? (
                                                    <ScrollEffect
                                                        key={index}
                                                        inViewProperties={{
                                                            y: [10, 0],
                                                            opacity: [0, 1],
                                                        }}
                                                        inViewDelay={index * 0.05}
                                                        css={{ width: '100%' }}
                                                    >
                                                        <button
                                                            key={index}
                                                            onClick={() => {
                                                                setMenuLevel(prev => prev + 1);
                                                                setSelectedMenuList(prev => [
                                                                    ...prev,
                                                                    subMenuItem?.label,
                                                                ]);
                                                                setMenuChildren(subMenuItem?.childMenu);
                                                                setSelectedIndicesList(prev => [...prev, index]);
                                                            }}
                                                            css={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderBottom: '1px solid #e2e2e2',
                                                                marginBottom: '15px',
                                                                paddingBottom: '15px',
                                                                justifyContent: 'space-between',
                                                                flexDirection: 'row',
                                                                color: 'var(--color-gray-schwarz)',
                                                            }}
                                                        >
                                                            <span
                                                                css={{
                                                                    fontWeight: 600,
                                                                    paddingRight: '5px',
                                                                }}
                                                            >
                                                                {subMenuItem.label}
                                                            </span>
                                                            <ArrowRightIcon css={{ width: '21px' }} />
                                                        </button>
                                                    </ScrollEffect>
                                                ) : (
                                                    <ScrollEffect
                                                        key={index}
                                                        inViewProperties={{
                                                            y: [10, 0],
                                                            opacity: [0, 1],
                                                        }}
                                                        inViewDelay={index * 0.05}
                                                        css={{ width: '100%' }}
                                                    >
                                                        <Link
                                                            to={subMenuItem.url}
                                                            onClick={() => {
                                                                setOpen(false);
                                                            }}
                                                            css={{ width: '100%' }}
                                                        >
                                                            <div
                                                                key={index}
                                                                css={{
                                                                    width: '100%',
                                                                    height: 'auto',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid #e2e2e2',
                                                                    marginBottom: '15px',
                                                                    paddingBottom: '15px',
                                                                    justifyContent: 'space-between',
                                                                    flexDirection: 'row',
                                                                    color: 'var(--color-gray-schwarz)',
                                                                }}
                                                            >
                                                                <span
                                                                    css={{
                                                                        fontWeight: 600,
                                                                        paddingRight: '5px',
                                                                    }}
                                                                >
                                                                    {subMenuItem.label}
                                                                </span>
                                                                {!subMenuItem.url.startsWith('/') && <MobileLinkIcon css={{ width: '21px', color: 'var(--color-gray-grau)' }} />}
                                                            </div>
                                                        </Link>
                                                    </ScrollEffect>
                                                )}
                                            </>
                                        ))}
                                    </div>
                                    {/* <div */}
                                    {/*    css={{ */}
                                    {/*        width: 'auto', */}
                                    {/*        height: 'auto', */}
                                    {/*        display: 'flex', */}
                                    {/*        alignItems: 'center', */}
                                    {/*        marginBottom: '14px', */}
                                    {/*    }} */}
                                    {/* > */}
                                    {/*    <BookIcon /> */}
                                    {/*    <span */}
                                    {/*        css={{ */}
                                    {/*            marginLeft: '10px', */}
                                    {/*            fontWeight: 600, */}
                                    {/*        }} */}
                                    {/*    > */}
                                    {/*        Leichte Sprache */}
                                    {/*    </span> */}
                                    {/* </div> */}
                                    {/* <div onClick={switchTheme} */}
                                    {/*    css={{ */}
                                    {/*        width: 'auto', */}
                                    {/*        height: 'auto', */}
                                    {/*        display: 'flex', */}
                                    {/*        alignItems: 'center', */}
                                    {/*    }} */}
                                    {/* > */}
                                    {/*    <ContrastIcon /> */}
                                    {/*    <span */}
                                    {/*        css={{ */}
                                    {/*            marginLeft: '12px', */}
                                    {/*            fontWeight: 600, */}
                                    {/*        }} */}
                                    {/*    > */}
                                    {/*        Kontrast */}
                                    {/*    </span> */}
                                    {/* </div> */}
                                </div>
                            </div>
                        </Container>
                    </DrawerContent>
                </Drawer>
            )}
        </>
    );
};

export default MobileHeader;
